import { forwardRef, type InputHTMLAttributes } from 'react'
import { cva, type VariantProps } from 'class-variance-authority'
import { cn } from '@/lib/utils'

const inputVariants = cva(
  // Base styles - accessible, clean design
  [
    'w-full bg-white text-surface-900',
    'border-2 border-surface-200',
    'transition-all duration-200 ease-out',
    'placeholder:text-surface-400',
    'focus:outline-none focus:border-accent-500 focus:ring-2 focus:ring-accent-500/20',
    'disabled:cursor-not-allowed disabled:bg-surface-50 disabled:text-surface-400',
  ],
  {
    variants: {
      size: {
        sm: 'h-9 px-3 text-sm rounded-lg',
        md: 'h-11 px-4 text-base rounded-xl',
        lg: 'h-13 px-5 text-lg rounded-xl',
      },
      error: {
        true: [
          'border-danger-500',
          'focus:border-danger-500 focus:ring-danger-500/20',
        ],
      },
    },
    defaultVariants: {
      size: 'md',
    },
  }
)

export interface InputProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size'>,
    VariantProps<typeof inputVariants> {
  label?: string
  helperText?: string
  errorText?: string
  leftIcon?: React.ReactNode
  rightIcon?: React.ReactNode
}

const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      className,
      size,
      error,
      label,
      helperText,
      errorText,
      leftIcon,
      rightIcon,
      id,
      ...props
    },
    ref
  ) => {
    const inputId = id || label?.toLowerCase().replace(/\s+/g, '-')

    return (
      <div className="w-full space-y-1.5">
        {label && (
          <label
            htmlFor={inputId}
            className="block text-sm font-medium text-surface-700"
          >
            {label}
          </label>
        )}
        <div className="relative">
          {leftIcon && (
            <div className="absolute left-3 top-1/2 -translate-y-1/2 text-surface-400">
              {leftIcon}
            </div>
          )}
          <input
            ref={ref}
            id={inputId}
            className={cn(
              inputVariants({ size, error: !!error || !!errorText, className }),
              leftIcon && 'pl-10',
              rightIcon && 'pr-10'
            )}
            aria-invalid={!!error || !!errorText}
            aria-describedby={
              errorText ? `${inputId}-error` : helperText ? `${inputId}-helper` : undefined
            }
            {...props}
          />
          {rightIcon && (
            <div className="absolute right-3 top-1/2 -translate-y-1/2 text-surface-400">
              {rightIcon}
            </div>
          )}
        </div>
        {errorText && (
          <p id={`${inputId}-error`} className="text-sm text-danger-600">
            {errorText}
          </p>
        )}
        {helperText && !errorText && (
          <p id={`${inputId}-helper`} className="text-sm text-surface-500">
            {helperText}
          </p>
        )}
      </div>
    )
  }
)

Input.displayName = 'Input'

// Textarea variant
export interface TextareaProps
  extends Omit<React.TextareaHTMLAttributes<HTMLTextAreaElement>, 'size'>,
    VariantProps<typeof inputVariants> {
  label?: string
  helperText?: string
  errorText?: string
}

const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
  ({ className, size, error, label, helperText, errorText, id, ...props }, ref) => {
    const inputId = id || label?.toLowerCase().replace(/\s+/g, '-')

    return (
      <div className="w-full space-y-1.5">
        {label && (
          <label
            htmlFor={inputId}
            className="block text-sm font-medium text-surface-700"
          >
            {label}
          </label>
        )}
        <textarea
          ref={ref}
          id={inputId}
          className={cn(
            inputVariants({ size, error: !!error || !!errorText, className }),
            'min-h-[120px] py-3 resize-y'
          )}
          aria-invalid={!!error || !!errorText}
          aria-describedby={
            errorText ? `${inputId}-error` : helperText ? `${inputId}-helper` : undefined
          }
          {...props}
        />
        {errorText && (
          <p id={`${inputId}-error`} className="text-sm text-danger-600">
            {errorText}
          </p>
        )}
        {helperText && !errorText && (
          <p id={`${inputId}-helper`} className="text-sm text-surface-500">
            {helperText}
          </p>
        )}
      </div>
    )
  }
)

Textarea.displayName = 'Textarea'

export { Input, Textarea, inputVariants }
